import React, { useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faStar as faStarSolid } from "@fortawesome/free-solid-svg-icons"
import { faStar as faStarOutline } from "@fortawesome/free-regular-svg-icons"
import { getNamespaces, getScopedVars, GLOBAL_NAMESPACE, publish, usePubsub } from "@/redux/pubsub"
import { useSelector } from "@/store"
import { mergeClasses } from "@/helpers/styling"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { SquarePen } from "lucide-react"
import { Item, ItemActions, ItemLabel, ItemList, MiniButton, SearchInput } from "@/components/ui-primitives/small"

type FavState = {
  favoriteVariables: string[]
  toggleFavoriteVariable: (variable: string) => void
}
type VarEntry = { name: string; value: unknown }

const useFavoriteVariablesStore = create<FavState>()(
  persist(
    (set, get) => ({
      favoriteVariables: [],
      toggleFavoriteVariable: (variable) => {
        const { favoriteVariables } = get()
        set({
          favoriteVariables: favoriteVariables.includes(variable)
            ? favoriteVariables.filter((v) => v !== variable)
            : [...favoriteVariables, variable],
        })
      },
    }),
    {
      name: "favorite-variables-store-v1",
    }
  )
)

export function AppVariables() {
  const pubsub = useSelector((s) => s.rootAppVars_INTERNAL ?? ({} as ReturnType<typeof Object.create>))

  const globalVars = useMemo<VarEntry[]>(
    () =>
      Object.entries(pubsub)
        .filter(([k]) => typeof k === "string")
        .map(([name, value]) => ({ name, value })),
    [pubsub]
  )

  const namespaces = useMemo(() => getNamespaces(pubsub), [pubsub])

  const namespacedVars = useMemo<Record<string, VarEntry[]>>(() => {
    const result: Record<string, VarEntry[]> = {}
    for (const ns of namespaces) {
      const scoped = getScopedVars(pubsub, ns)
      result[ns] = scoped ? Object.entries(scoped).map(([name, value]) => ({ name, value })) : []
    }
    return result
  }, [pubsub, namespaces])

  const [searchTerm, setSearchTerm] = useState("")
  const [selectedNamespaces, setSelectedNamespaces] = useState<Set<string>>(
    () => new Set(["global", ...namespaces])
  )
  const favoriteVariables = useFavoriteVariablesStore((s) => s.favoriteVariables)
  const [expandedVar, setExpandedVar] = useState<string | null>(null)

  const allNamespaceKeys: string[] = ["global", ...namespaces]

  useEffect(() => {
    setSelectedNamespaces((prev) => {
      const next = new Set(prev)
      for (const ns of namespaces) next.add(ns)
      return next
    })
  }, [namespaces])

  function toggleNamespace(key: string) {
    setSelectedNamespaces((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function filterAndSort(vars: VarEntry[]): VarEntry[] {
    let result = vars
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase()
      result = vars.filter((v) => v.name.toLowerCase().includes(lower) || String(v.value).toLowerCase().includes(lower))
    }
    return result.sort((a, b) => {
      const af = favoriteVariables.includes(a.name)
      const bf = favoriteVariables.includes(b.name)
      if (af && !bf) return -1
      if (!af && bf) return 1
      return a.name.localeCompare(b.name)
    })
  }

  const sections: Array<{ key: string; label: string; vars: VarEntry[] }> = [
    ...(selectedNamespaces.has("global") ? [{ key: "global", label: "global", vars: filterAndSort(globalVars) }] : []),
    ...namespaces
      .filter((ns) => selectedNamespaces.has(ns))
      .map((ns) => ({ key: ns, label: ns, vars: filterAndSort(namespacedVars[ns] ?? []) })),
  ]

  const hasAnyResults = sections.some((s) => s.vars.length > 0)
  const showNamespaceBar = allNamespaceKeys.length > 1

  return (
    <div className="flex flex-col flex-grow overflow-hidden max-h-full">
      <div className="p-2 flex-shrink-0 border-b border-border flex flex-col gap-1.5">
        <SearchInput placeholder="Search variables..."
                     value={searchTerm}
                     onChange={setSearchTerm} />
        {showNamespaceBar && (
          <div className="flex flex-wrap gap-1">
            {allNamespaceKeys.map((key) => (
              <button
                key={key}
                onClick={() => toggleNamespace(key)}
                className={mergeClasses("text-[10.5px] px-2 py-0.5 rounded-full border transition-colors", {
                  "bg-sky-100 border-sky-300 text-sky-700": selectedNamespaces.has(key),
                  "bg-white border-stone-300 text-stone-400": !selectedNamespaces.has(key),
                })}
              >
                {key}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto bg-white" style={{ scrollbarWidth: "thin" }}>
        {!hasAnyResults ? (
          <div className="text-center text-gray-400 p-3">
            {searchTerm ? `No variables found matching "${searchTerm}"` : "No variables found"}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sections.map(({ key, label, vars }) =>
              vars.length === 0 ? null : (
                <NamespaceSection key={key} label={label} showHeader={showNamespaceBar}>
                  <ItemList>
                    {vars.map((variable, index) => (
                      <AppVariableItem
                        key={`${variable.name}-${index}`}
                        variable={variable}
                        namespace={key}
                        expandedVar={expandedVar}
                        setExpandedVar={setExpandedVar}
                      />
                    ))}
                  </ItemList>
                </NamespaceSection>
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function NamespaceSection(props: { label: string; showHeader: boolean; children: React.ReactNode }) {
  if (!props.showHeader) return <>{props.children}</>
  return (
    <div className="border-l-2 border-sky-400 ml-1.5 rounded-r">
      <div className="text-[10px] font-medium text-sky-700 bg-sky-50 border-b border-sky-100 px-2 py-0.5">
        {props.label}
      </div>
      {props.children}
    </div>
  )
}

function AppVariableItem(props: {
  variable: VarEntry
  namespace: string
  expandedVar: string | null
  setExpandedVar: (name: string) => void
}) {
  const expanded = props.expandedVar === props.variable.name
  const [, publishFn] = usePubsub()
  const isFavorite = useFavoriteVariablesStore((s) => s.favoriteVariables.includes(props.variable.name))
  const toggleFavoriteVariable = useFavoriteVariablesStore((s) => s.toggleFavoriteVariable)

  return (
    <Item
      onClick={() => props.setExpandedVar(props.variable.name)}
      className={mergeClasses("border-gray-200 hover:bg-gray-100 group select-auto", {
        "cursor-pointer select-none": !expanded,
      })}
    >
      <ItemLabel>
        <div>
          <button
            type="button"
            className={mergeClasses("rounded", {
              "text-yellow-600 hover:text-yellow-700": isFavorite,
              "text-gray-400 hover:text-yellow-600": !isFavorite,
            })}
            onClick={() => toggleFavoriteVariable(props.variable.name)}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            <FontAwesomeIcon icon={isFavorite ? faStarSolid : faStarOutline} className="text-[10px]" />
          </button>
        </div>
        <div
          className={mergeClasses("overflow-hidden", {
            "whitespace-pre-wrap break-all": expanded,
            "whitespace-nowrap text-ellipsis": !expanded,
          })}
        >
          <span>
            <span
              className={mergeClasses({
                "font-bold": isFavorite,
                "select-all": expanded,
                "select-none": !expanded,
              })}
            >
              {props.variable.name}
            </span>
            <span
              className={mergeClasses("select-all text-violet-600 ml-2", {
                "select-all": expanded,
                "select-none": !expanded,
              })}
            >
              {formatValue(props.variable.value)}
            </span>
          </span>
        </div>
      </ItemLabel>
      <ItemActions>
        <MiniButton
          title="Edit value"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => {
            const newValue = prompt("value to set", props.variable.value ? String(props.variable.value) : "")
            if (newValue == null) return
            const ns = props.namespace === "global" ? GLOBAL_NAMESPACE : props.namespace
            publish(props.variable.name, newValue, ns, "app-variables manual edit")
          }}
        >
          <SquarePen size={12} />
        </MiniButton>
      </ItemActions>
    </Item>
  )
}

function formatValue(value: unknown): string {
  if (value === null) return "null"
  if (value === undefined) return "undefined"
  if (typeof value === "object") return JSON.stringify(value, null, 2)
  return String(value)
}
